import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';
import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';

// ─── Constants (unchanged) ────────────────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = 'AIzaSyAivpJIUGv7J-LCuYdlw7Crbm1M2Snj8A0';
const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };
const MAP_LIBRARIES = ['drawing', 'geometry'];

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

// ─── Dark map style ───────────────────────────────────────────────────────────
const MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#1a2744' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3f7' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a2744' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#162032' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry.fill', stylers: [{ color: '#1a3a2a' }, { visibility: 'on' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#253d5e' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#8faec8' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2d5a8e' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#a8c8e8' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0d1f3c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a7fa5' }] },
];

const MAP_OPTIONS = {
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  styles: MAP_STYLE,
};

// ─── Custom SVG marker icons ──────────────────────────────────────────────────
const DEVICE_ICON = {
  url:
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <ellipse cx="18" cy="40" rx="8" ry="3" fill="rgba(0,0,0,0.3)"/>
        <path d="M18 2 C10 2 4 8.5 4 16.5 C4 27 18 40 18 40 C18 40 32 27 32 16.5 C32 8.5 26 2 18 2Z"
              fill="#22c55e" filter="url(#glow)" stroke="#16a34a" stroke-width="1.5"/>
        <circle cx="18" cy="16" r="6" fill="white" opacity="0.95"/>
        <circle cx="18" cy="16" r="3" fill="#22c55e"/>
      </svg>
    `),
  scaledSize: { width: 36, height: 44 },
  anchor: { x: 18, y: 44 },
};

const FALLBACK_ICON = {
  url:
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
        <ellipse cx="16" cy="37" rx="7" ry="2.5" fill="rgba(0,0,0,0.25)"/>
        <path d="M16 2 C9 2 3 8 3 15.5 C3 25 16 38 16 38 C16 38 29 25 29 15.5 C29 8 23 2 16 2Z"
              fill="#eab308" stroke="#ca8a04" stroke-width="1.5"/>
        <circle cx="16" cy="15" r="5.5" fill="white" opacity="0.9"/>
        <circle cx="16" cy="15" r="2.5" fill="#eab308"/>
      </svg>
    `),
  scaledSize: { width: 32, height: 40 },
  anchor: { x: 16, y: 40 },
};

// ─── Geocode helper (unchanged) ───────────────────────────────────────────────
function geocodeAddress(geocoder, address) {
  return new Promise((resolve) => {
    geocoder.geocode({ address: `${address}, India` }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        resolve(null);
      }
    });
  });
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function MapDevices() {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const geocoderRef = useRef(null);
  const mapRef      = useRef(null);

  // ── State (data) ──────────────────────────────────────────────────────────
  const [pins, setPins]         = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // ── State (UI) ────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [zoom, setZoom]     = useState(5);

  const onMapLoad = useCallback((map) => {
    mapRef.current      = map;
    geocoderRef.current = new window.google.maps.Geocoder();
  }, []);

  const onZoomChanged = useCallback(() => {
    if (mapRef.current) setZoom(mapRef.current.getZoom() ?? 5);
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!isLoaded) return;
    try {
      setLoading(true);
      setError('');
      const { data } = await axiosInstance.get('/super-admin/device-locations');
      const { deviceLocations = [], partnerFallbacks = [] } = data;

      const exactPins = deviceLocations.map((d) => ({
        id:          `dl-${d.device_id}`,
        lat:         parseFloat(d.latitude),
        lng:         parseFloat(d.longitude),
        label:       d.device_id,
        partnerName: d.partner_name,
        city:        d.city,
        state:       d.state,
        isFallback:  false,
      }));

      const fallbackPins = [];
      for (const p of partnerFallbacks) {
        const address = [p.city, p.state].filter(Boolean).join(', ');
        if (!address) continue;
        const coords = await geocodeAddress(geocoderRef.current, address);
        if (coords) {
          fallbackPins.push({
            id:          `fb-${p.partner_id}`,
            lat:         coords.lat,
            lng:         coords.lng,
            label:       'Partner Location',
            partnerName: p.partner_name,
            city:        p.city,
            state:       p.state,
            isFallback:  true,
          });
        }
        await new Promise((r) => setTimeout(r, 120));
      }

      setPins([...exactPins, ...fallbackPins]);
    } catch (err) {
      console.error('MapDevices load error:', err);
      setError('Failed to load device locations. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, [isLoaded]);

  useEffect(() => { load(); }, [load]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const exactCount   = pins.filter((p) => !p.isFallback).length;
  const fallbackCount = pins.filter((p) => p.isFallback).length;

  const searchTerm  = search.trim().toLowerCase();
  const visiblePins = searchTerm
    ? pins.filter((p) =>
        p.partnerName?.toLowerCase().includes(searchTerm) ||
        p.label?.toLowerCase().includes(searchTerm)
      )
    : pins;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Wrapper>
      <style>{`
        @keyframes _md_pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:0.5} }
        @keyframes _md_blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      {/* ── Dark gradient command-bar header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        height: 72,
        padding: '0 24px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        borderRadius: 12,
        marginBottom: 8,
        flexShrink: 0,
      }}>
        {/* Left: live indicator + title */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#22c55e',
              boxShadow: '0 0 6px #22c55e',
              animation: '_md_blink 1.5s ease-in-out infinite',
              display: 'inline-block',
            }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#22c55e', letterSpacing: '0.1em' }}>
              LIVE
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: '#ffffff', lineHeight: 1.2 }}>
            Device Network
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            Real-time partner device locations across India
          </div>
        </div>

        {/* Right: stat chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { dot: '#22c55e', label: 'Active Devices',    count: exactCount },
            { dot: '#eab308', label: 'Partner Fallbacks', count: fallbackCount },
            { dot: '#e2e8f0', label: 'Total Pins',        count: exactCount + fallbackCount },
          ].map(({ dot, label, count }) => (
            <div key={label} style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 20,
              padding: '6px 14px',
              fontSize: 13,
              color: '#e2e8f0',
              whiteSpace: 'nowrap',
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: dot, flexShrink: 0,
                boxShadow: dot === '#22c55e' ? '0 0 5px #22c55e' : 'none',
              }} />
              <span style={{ fontWeight: 600 }}>{count}</span>
              <span style={{ color: '#94a3b8' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Map area ── */}
      <div style={{
        flex: 1,
        minHeight: 0,
        height: 'calc(100vh - 180px)',
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #1e293b',
        boxShadow: '0 4px 32px rgba(0,0,0,0.4)',
        position: 'relative',
      }}>

        {/* Loading overlay */}
        {(!isLoaded || loading) && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 20,
            background: 'rgba(15,23,42,0.85)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 16,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: '#22c55e',
              animation: '_md_pulse 1.5s ease-in-out infinite',
            }} />
            <span style={{ color: '#ffffff', fontSize: 14, fontWeight: 500 }}>
              Fetching device locations…
            </span>
          </div>
        )}

        {/* Error overlay */}
        {!loading && error && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 20,
            background: 'rgba(15,23,42,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#0f172a',
              border: '1px solid #ef4444',
              borderRadius: 12,
              padding: '24px 32px',
              textAlign: 'center',
              maxWidth: 340,
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 14 }}>
                <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.5" />
                <path d="M12 7v5" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="#ef4444" />
              </svg>
              <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
                Failed to load device locations
              </div>
              <div style={{ color: '#64748b', fontSize: 13, marginBottom: 18 }}>
                Please check your connection and try again.
              </div>
              <button
                onClick={load}
                style={{
                  background: '#22c55e', color: '#ffffff',
                  border: 'none', borderRadius: 8,
                  padding: '8px 20px', fontSize: 14,
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Floating search bar */}
        {isLoaded && (
          <div style={{
            position: 'absolute', top: 16, left: '50%',
            transform: 'translateX(-50%)', zIndex: 10,
          }}>
            <div style={{ position: 'relative' }}>
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              >
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search partner or device…"
                style={{
                  width: 320,
                  paddingLeft: 40, paddingRight: 16,
                  paddingTop: 10, paddingBottom: 10,
                  fontSize: 14,
                  background: 'rgba(15,23,42,0.9)',
                  border: '1px solid #334155',
                  borderRadius: 24,
                  color: '#ffffff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        )}

        {/* Google Map */}
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={INDIA_CENTER}
            zoom={5}
            options={MAP_OPTIONS}
            onLoad={onMapLoad}
            onZoomChanged={onZoomChanged}
            onClick={() => setSelected(null)}
          >
            {visiblePins.map((pin) => {
              const useDot    = zoom < 10;
              const dotColor  = pin.isFallback ? '#eab308' : '#22c55e';

              return (
                <MarkerF
                  key={pin.id}
                  position={{ lat: pin.lat, lng: pin.lng }}
                  icon={
                    useDot
                      ? {
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 8,
                          fillColor: dotColor,
                          fillOpacity: 0.92,
                          strokeColor: '#ffffff',
                          strokeWeight: 1.5,
                          strokeOpacity: 1,
                        }
                      : pin.isFallback ? FALLBACK_ICON : DEVICE_ICON
                  }
                  title={`${pin.partnerName} — ${pin.label}`}
                  onClick={() => setSelected(pin)}
                />
              );
            })}

            {selected && (
              <InfoWindowF
                position={{ lat: selected.lat, lng: selected.lng }}
                onCloseClick={() => setSelected(null)}
                options={{ disableAutoPan: false, pixelOffset: new window.google.maps.Size(0, -10) }}
              >
                <div style={{
                  background: '#0f172a',
                  borderRadius: 10,
                  padding: '14px 16px',
                  minWidth: 220,
                  border: '1px solid #334155',
                  color: '#ffffff',
                  fontFamily: 'inherit',
                }}>
                  <span style={{
                    display: 'inline-block',
                    fontSize: 10, fontWeight: 600,
                    padding: '3px 8px', borderRadius: 10,
                    background: selected.isFallback ? '#422006' : '#14532d',
                    color: selected.isFallback ? '#fde68a' : '#86efac',
                  }}>
                    {selected.isFallback ? 'Partner Fallback' : 'Device Location'}
                  </span>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff', marginTop: 8 }}>
                    {selected.label}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                    {selected.partnerName}
                  </div>
                  {(selected.city || selected.state) && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {[selected.city, selected.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b', marginTop: 6 }}>
                    {selected.lat.toFixed(5)}, {selected.lng.toFixed(5)}
                  </div>
                  <div style={{ borderTop: '1px solid #1e293b', marginTop: 10, paddingTop: 8 }}>
                    <a
                      href={`https://www.google.com/maps?q=${selected.lat},${selected.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 11, color: '#60a5fa', textDecoration: 'none' }}
                    >
                      📍 View on Google Maps
                    </a>
                  </div>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        )}

        {/* Floating legend panel */}
        {isLoaded && !loading && (
          <div style={{
            position: 'absolute', bottom: 24, left: 24, zIndex: 10,
            background: 'rgba(15,23,42,0.92)',
            border: '1px solid #334155',
            borderRadius: 12,
            padding: '14px 18px',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: '#64748b',
              letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10,
            }}>
              Legend
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                  background: '#22c55e', boxShadow: '0 0 6px #22c55e',
                  display: 'inline-block',
                }} />
                <span style={{ fontSize: 13, color: '#e2e8f0' }}>Device Location</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                  background: '#eab308', boxShadow: '0 0 6px #eab308',
                  display: 'inline-block',
                }} />
                <span style={{ fontSize: 13, color: '#e2e8f0' }}>Partner Fallback</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
